import type { Locale } from "../templates/types";

/**
 * Hand-rolled i18n table (§15 of the plan — i18next was considered and
 * rejected as unnecessary weight for ~200 strings). English values here are
 * the EXISTING wording lifted verbatim from the components that used to
 * hardcode them — this file must never be the place a wording change slips
 * in disguised as a refactor. Spanish values are genuine, natural
 * translations, not machine-literal ones.
 *
 * Field ids, labels, and promptHints are NOT here — those are template data
 * (see templates/familyWellness.ts) and already carry their own
 * `Record<Locale, string>` shape. This table is for UI chrome only: button
 * labels, headings, static copy. It does not carry the Rails-authored fixed
 * safety message (`safety.message` on the wire) — that is server content,
 * never client-owned (§21 non-negotiable #5).
 */
const EN = {
  // App shell — header tabs and locale toggle (App.tsx)
  "tabs.run": "Run a check-in",
  "tabs.design": "Design your own",
  "tabs.chw": "CHW view",
  "tabs.ariaLabel": "Main views",
  "localeToggle.ariaLabel": "Language",
  "localeToggle.en": "English",
  "localeToggle.es": "Español",

  // RunCheckIn.tsx — idle start screen and the completion banner
  "start.heading": "Family Wellness Check-in",
  "start.body": "A short, conversational check-in — about six questions. You can stop at any time.",
  "start.button": "Start check-in",
  "complete.banner":
    "This check-in is complete. Review the structured data on the right, then export it from the governance panel below.",
  "complete.button": "Start a new check-in",

  // Mobile segmented control (<1024px) — §17
  "runCheckIn.mobilePane.ariaLabel": "Panel view",
  "runCheckIn.mobilePane.conversation": "Conversation",
  "runCheckIn.mobilePane.data": "Data {filled}/{total}",

  // Chat pane / composer / message bubbles / typing indicator
  "chat.placeholder.default": "Type your answer…",
  "chat.placeholder.paused": "This check-in is paused.",
  "chat.composer.srLabel": "Your answer",
  "chat.composer.send": "Send",
  "chat.message.aiAgent": "AI agent",
  "chat.message.viaVoice": "via voice",
  "chat.typing.ariaLabel": "The agent is typing",

  // Field pane / field card
  "fields.pane.heading": "Structured data",
  "fields.status.unset": "Not yet asked",
  "fields.status.filled": "Filled",
  "fields.status.needsConfirmation": "Needs confirmation",
  "fields.status.unknown": "Unknown",
  "fields.status.skipped": "Skipped",
  "fields.value.notesRecordedOne": "{n} note recorded",
  "fields.value.notesRecordedOther": "{n} notes recorded",

  // Shared yes/no (boolean field value display, and the provenance "reviewed" flag)
  "common.yes": "Yes",
  "common.no": "No",

  // Provenance popover
  "provenance.empty": "No provenance recorded yet for this field.",
  "provenance.source.llmExtraction": "Extracted from the conversation",
  "provenance.source.humanEdit": "Edited by hand",
  "provenance.source.engineDefault": "Engine default",
  "provenance.label.source": "Source",
  "provenance.label.quote": "Quote",
  "provenance.label.turn": "Turn",
  "provenance.label.confidence": "Confidence",
  "provenance.label.inputMode": "Input mode",
  "provenance.label.reviewed": "Reviewed by a human",
  "provenance.inputMode.voice": "Voice (speech-to-text)",
  "provenance.inputMode.typed": "Typed",
  "provenance.ariaLabelFor": "Provenance for {field}",
  "provenance.ariaLabelGeneric": "Field provenance",

  // Safety banner / crisis resources — UI chrome ONLY. The disclosure-triggered
  // `safety.message` itself is Rails-authored (§11, §21 #5) and is rendered
  // as-is; it is never looked up in this table.
  // Not a promise of follow-up: this prototype has no hand-off (human QA, 2026-10-03).
  "safety.banner.text": "This check-in is paused. This demo does not contact anyone. If you need help now, use the resources below.",
  "safety.readAloud.button": "🔊 Read aloud",
  "safety.readAloud.caption": "Nothing is spoken automatically.",
  // No hand-off exists in this prototype (human QA, 2026-10-03); the label must not imply one.
  "safety.button.end": "End check-in (no hand-off in this demo)",
  "safety.button.restart": "Start new check-in",
  "safety.handoff.notInPrototype":
    "Not in this prototype: a real deployment would notify the assigned community health worker here. Nothing has been sent. The record, with follow-up flagged, can be exported from the governance panel below.",
  "safety.crisisResources.framing":
    "These are general US crisis resources. A real deployment should substitute community-approved local resources.",

  // Governance panel / export bar / mode badge
  "governance.heading": "Governance & data handling",
  "governance.collapse": "Collapse",
  "governance.summary.live": "Live",
  "governance.summary.demo": "Demo Mode",
  "governance.summary.template": "Governance & data handling — {mode} · click to expand",
  "governance.persistence":
    "Fieldnote has no application-side persistence of interview data. No database, no accounts, no server-side session; answers live in this browser tab only and are discarded on refresh or close.",
  "governance.liveDataStatement":
    "In Live mode, your text is sent to Google's Gemini API to generate the next question, and is not retained by Fieldnote. In Demo Mode no AI model is called, but your text still goes to Fieldnote's server, which checks it for a safety disclosure and picks the next scripted reply without keeping it. If you use the microphone, your browser's own speech service turns speech into text, and may do so on its maker's servers.",
  // §13 fixed policy language — translated faithfully, not paraphrased.
  "governance.dataPolicy.unspecified":
    "This deployment has not declared its data-handling terms. Whether Google may use API input to improve its models depends on the billing tier and configuration of the API key in use. Verify before collecting real participant data.",
  "governance.dataPolicy.paidTier":
    "The operator has configured this deployment against a paid-tier key, whose terms state input is not used to improve Google's models. Verify against the current terms for your project.",
  "governance.dataPolicy.freeTier":
    "This deployment uses a free-tier key. Google's terms permit using input to improve its models. Do not enter real participant data.",
  "governance.googleTermsLink": "Google's terms",
  "governance.customDeclared": "Declared by this deployment's operator.",
  "governance.cabNote":
    "Production scripts should be approved by the community advisory board and tribal IRB where applicable.",
  "governance.downloadCsv": "Download CSV",
  "governance.downloadJson": "Download JSON",
  "governance.exportGroupAriaLabel": "Export interview data",

  // AI summary (Tier 3, §5, §8) — always tagged "AI-generated", isolated
  // from participant data both on screen and in export.ts's
  // ai_summary_not_participant_data column.
  "summary.generateButton": "Generate AI summary",
  "summary.generating": "Generating…",
  "summary.badge": "AI-generated — not participant data",
  // Demo Mode's summary is a deterministic template, not model output, so
  // the "AI-generated" badge would be false there (§21 item 12).
  "summary.badgeDemo": "Demo summary, written without a model — not participant data",
  "summary.error": "Could not generate a summary. You can still export without one.",
  "summary.pausedNote": "Not available after a safety pause: no model is called once a check-in is paused.",

  "modeBadge.demoDegraded": "Demo response (model unavailable)",
  "modeBadge.checking": "Checking mode…",
  "modeBadge.live": "Live — Google Gemini",
  // Not "no data leaves this device": even in Demo Mode every turn is POSTed
  // to Rails, because the safety pre-scan and the pause verdict live on the
  // server (§4, §11), and browser dictation may use the browser vendor's
  // servers. The badge claims only what Demo Mode guarantees.
  "modeBadge.demo": "Demo Mode — no AI model is called",

  // Voice (§16) — MicButton aria-labels and the unsupported-browser fallback
  // line. The mic itself is never shown disabled/broken (§21 #12): it's
  // either present and working, or absent with this one calm sentence.
  "voice.mic.start": "Start voice input",
  "voice.mic.stop": "Stop voice input",
  "voice.unsupported": "Voice input isn't supported in this browser. Typing works everywhere.",
} as const;

export type StringKey = keyof typeof EN;

// `Record<StringKey, string>` makes a missing Spanish entry a TYPE ERROR at
// compile time — the strongest form of the completeness guarantee. The
// runtime test in __tests__/strings.test.ts asserts the same thing so the
// guarantee survives someone reaching for an `as any` under deadline pressure.
const ES: Record<StringKey, string> = {
  "tabs.run": "Hacer un chequeo",
  "tabs.design": "Diseño propio",
  "tabs.chw": "Vista CHW",
  "tabs.ariaLabel": "Vistas principales",
  "localeToggle.ariaLabel": "Idioma",
  "localeToggle.en": "English",
  "localeToggle.es": "Español",

  "start.heading": "Chequeo de bienestar familiar",
  "start.body": "Un chequeo breve y conversacional — unas seis preguntas. Puede detenerse en cualquier momento.",
  "start.button": "Iniciar chequeo",
  "complete.banner":
    "Este chequeo ha terminado. Revise los datos estructurados a la derecha y luego expórtelos desde el panel de gobernanza de datos, abajo.",
  "complete.button": "Iniciar un nuevo chequeo",

  "runCheckIn.mobilePane.ariaLabel": "Vista del panel",
  "runCheckIn.mobilePane.conversation": "Conversación",
  "runCheckIn.mobilePane.data": "Datos {filled}/{total}",

  "chat.placeholder.default": "Escriba su respuesta…",
  "chat.placeholder.paused": "Este chequeo está pausado.",
  "chat.composer.srLabel": "Su respuesta",
  "chat.composer.send": "Enviar",
  "chat.message.aiAgent": "Agente de IA",
  "chat.message.viaVoice": "por voz",
  "chat.typing.ariaLabel": "El agente está escribiendo",

  "fields.pane.heading": "Datos estructurados",
  "fields.status.unset": "Aún no preguntado",
  "fields.status.filled": "Completado",
  "fields.status.needsConfirmation": "Necesita confirmación",
  "fields.status.unknown": "Desconocido",
  "fields.status.skipped": "Omitido",
  "fields.value.notesRecordedOne": "{n} nota registrada",
  "fields.value.notesRecordedOther": "{n} notas registradas",

  "common.yes": "Sí",
  "common.no": "No",

  "provenance.empty": "Aún no hay procedencia registrada para este campo.",
  "provenance.source.llmExtraction": "Extraído de la conversación",
  "provenance.source.humanEdit": "Editado manualmente",
  "provenance.source.engineDefault": "Valor predeterminado del motor",
  "provenance.label.source": "Fuente",
  "provenance.label.quote": "Cita",
  "provenance.label.turn": "Turno",
  "provenance.label.confidence": "Confianza",
  "provenance.label.inputMode": "Modo de entrada",
  "provenance.label.reviewed": "Revisado por una persona",
  "provenance.inputMode.voice": "Voz (conversión de voz a texto)",
  "provenance.inputMode.typed": "Escrito",
  "provenance.ariaLabelFor": "Procedencia de {field}",
  "provenance.ariaLabelGeneric": "Procedencia del campo",

  "safety.banner.text": "Esta entrevista está pausada. Esta demostración no contacta a nadie. Si necesita ayuda ahora, use los recursos de abajo.",
  "safety.readAloud.button": "🔊 Escuchar en voz alta",
  "safety.readAloud.caption": "Nada se reproduce automáticamente.",
  "safety.button.end": "Finalizar chequeo (esta demostración no deriva a nadie)",
  "safety.button.restart": "Iniciar nuevo chequeo",
  "safety.handoff.notInPrototype":
    "No incluido en este prototipo: una implementación real avisaría aquí al trabajador de salud comunitario asignado. No se ha enviado nada. El registro, con el seguimiento marcado, se puede exportar desde el panel de gobernanza de abajo.",
  "safety.crisisResources.framing":
    "Estos son recursos generales de crisis en EE. UU. Una implementación real debe sustituirlos por recursos locales aprobados por la comunidad.",

  "governance.heading": "Gobernanza y manejo de datos",
  "governance.collapse": "Contraer",
  "governance.summary.live": "En vivo",
  "governance.summary.demo": "Modo de demostración",
  "governance.summary.template": "Gobernanza y manejo de datos — {mode} · haga clic para expandir",
  "governance.persistence":
    "Fieldnote no conserva los datos de la entrevista del lado de la aplicación. No hay base de datos, ni cuentas, ni sesión en el servidor; las respuestas existen solo en esta pestaña del navegador y se descartan al actualizar o cerrar la página.",
  "governance.liveDataStatement":
    "En el modo En vivo, su texto se envía a la API de Gemini de Google para generar la siguiente pregunta, y Fieldnote no lo conserva. En el modo de demostración no se llama a ningún modelo de IA, pero su texto sí llega al servidor de Fieldnote, que lo revisa en busca de una revelación delicada y elige la siguiente respuesta predefinida sin conservarlo. Si usa el micrófono, el servicio de voz de su propio navegador convierte la voz en texto, y puede hacerlo en los servidores de su fabricante.",
  "governance.dataPolicy.unspecified":
    "Esta implementación no ha declarado sus términos de manejo de datos. Que Google pueda usar la información enviada a la API para mejorar sus modelos depende del nivel de facturación y la configuración de la clave de API en uso. Verifique esto antes de recopilar datos reales de participantes.",
  "governance.dataPolicy.paidTier":
    "El operador ha configurado esta implementación con una clave de nivel de pago, cuyos términos indican que la información enviada no se usa para mejorar los modelos de Google. Verifique esto con los términos vigentes de su proyecto.",
  "governance.dataPolicy.freeTier":
    "Esta implementación usa una clave de nivel gratuito. Los términos de Google permiten usar la información enviada para mejorar sus modelos. No ingrese datos reales de participantes.",
  "governance.googleTermsLink": "los términos de Google",
  "governance.customDeclared": "Declarado por el operador de esta implementación.",
  "governance.cabNote":
    "Los protocolos de producción deben ser aprobados por la junta asesora comunitaria y el IRB tribal cuando corresponda.",
  "governance.downloadCsv": "Descargar CSV",
  "governance.downloadJson": "Descargar JSON",
  "governance.exportGroupAriaLabel": "Exportar datos de la entrevista",

  "summary.generateButton": "Generar resumen de IA",
  "summary.generating": "Generando…",
  "summary.badge": "Generado por IA — no son datos del participante",
  "summary.badgeDemo": "Resumen de demostración, escrito sin un modelo — no son datos del participante",
  "summary.error": "No se pudo generar un resumen. Aún puede exportar sin uno.",
  "summary.pausedNote": "No disponible después de una pausa de seguridad: no se llama a ningún modelo una vez que se pausa una sesión.",

  "modeBadge.demoDegraded": "Respuesta de demostración (modelo no disponible)",
  "modeBadge.checking": "Comprobando el modo…",
  "modeBadge.live": "En vivo — Google Gemini",
  "modeBadge.demo": "Modo de demostración — no se llama a ningún modelo de IA",

  "voice.mic.start": "Iniciar entrada de voz",
  "voice.mic.stop": "Detener entrada de voz",
  "voice.unsupported": "La entrada de voz no es compatible con este navegador. Escribir funciona en todas partes.",
};

const STRINGS: Record<Locale, Record<StringKey, string>> = { en: EN, es: ES };

/**
 * Plain lookup function rather than a second React context: `state.locale`
 * already flows through the component tree (props or `useInterviewState()`
 * depending on the file — see LocaleContext.tsx), so every caller already
 * has a `Locale` value in scope. `t(locale, key)` is the entire API.
 *
 * `vars` does simple `{name}` interpolation for the handful of strings that
 * need it (e.g. "{n} notes recorded", the governance summary line) — no
 * pluralization library, callers pick the *_one/_other key themselves.
 */
export function t(locale: Locale, key: StringKey, vars?: Record<string, string | number>): string {
  const table = STRINGS[locale] ?? STRINGS.en;
  let value = table[key] ?? STRINGS.en[key] ?? key;
  if (vars) {
    for (const [name, replacement] of Object.entries(vars)) {
      value = value.split(`{${name}}`).join(String(replacement));
    }
  }
  return value;
}

export { STRINGS };
